import { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { scrollTo } from "seamless-scroll-polyfill";

import AscButton from "../AscButton/AscButton";
import { ArrowLeftIcon, ArrowRightIcon } from "../AscIcons";
import styles from "./AscCarouselList.module.scss";

export interface AscCarouselListProps {
  title?: string;
  children?: React.ReactNode;
  className?: string;
  hideControls?: boolean;
  responsiveOnly?: boolean | "sm" | "md";
  contentClassName?: string;
  scrollableContainerClassName?: string;
  loading?: boolean;
}

export default function AscCarouselList({
  title,
  children,
  className,
  hideControls = false,
  responsiveOnly = false,
  contentClassName,
  scrollableContainerClassName,
  loading = false,
}: AscCarouselListProps) {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollableRef.current;
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollThreshold, setScrollThreshold] = useState(0);
  const [prevButtonDisabled, setPrevButtonDisabled] = useState(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(false);

  const controlsNeeded = useMemo(
    () =>
      (scrollRef?.offsetWidth || 0) <= (itemsRef?.current?.offsetWidth || 0) &&
      (itemsRef?.current?.childNodes as NodeListOf<HTMLDivElement>)?.[0]
        ?.clientWidth *
        (itemsRef?.current?.childNodes?.length || 0) >=
        (itemsRef?.current?.offsetWidth || 0),
    [scrollRef?.offsetWidth]
  );

  useEffect(() => {
    setScrollThreshold((scrollRef?.clientWidth || 0) / 2);
  }, [scrollRef]);

  useEffect(() => {
    if (!scrollRef) {
      return;
    }
    scrollTo(scrollRef, {
      left: scrollPosition,
      behavior: "smooth",
    });
  }, [scrollPosition, scrollRef]);

  useEffect(() => {
    if (!scrollRef || hideControls) {
      return;
    }

    if (
      (scrollRef?.scrollLeft || 0) + (scrollRef?.clientWidth || 0) ===
      scrollRef?.scrollWidth
    ) {
      setNextButtonDisabled(true);
    } else {
      setNextButtonDisabled(false);
    }

    const onScroll = (scrollRef.onscroll = () => {
      if (
        (scrollRef?.scrollLeft || 0) + (scrollRef?.clientWidth || 0) ===
        scrollRef?.scrollWidth
      ) {
        setNextButtonDisabled(true);
      } else {
        setNextButtonDisabled(false);
      }
      if (scrollRef?.scrollLeft === 0) {
        setPrevButtonDisabled(true);
      } else {
        setPrevButtonDisabled(false);
      }
    });

    return () => {
      scrollRef?.removeEventListener?.("scroll", onScroll);
    };
  }, [scrollRef, hideControls]);

  function handleSlideRight() {
    setPrevButtonDisabled(false);
    if (
      scrollRef &&
      scrollPosition + scrollThreshold >=
        scrollRef?.scrollWidth - scrollRef?.clientWidth
    ) {
      setScrollPosition(scrollRef?.scrollWidth - scrollRef?.clientWidth);
      setNextButtonDisabled(true);
      return;
    }
    setScrollPosition(scrollPosition + scrollThreshold);
  }

  function handleSlideLeft() {
    setNextButtonDisabled(false);
    if (scrollPosition < scrollThreshold) {
      setScrollPosition(0);
      setPrevButtonDisabled(true);
      return;
    }
    setScrollPosition(scrollPosition - scrollThreshold);
  }

  return (
    <div
      data-testid="asc-carousel-list"
      className={classNames(
        styles.ground,
        {
          [styles.loading]: loading,
          [styles.smBreakPoint]:
            responsiveOnly === true || responsiveOnly === "sm",
          [styles.mdBreakpoint]: responsiveOnly === "md",
        },
        className
      )}
    >
      <div
        className={classNames(styles.header, {
          [styles.hideHeader]: hideControls && !title,
        })}
      >
        <div className={styles.title}>{title}</div>
        <div
          className={classNames(styles.navigation, {
            [styles.hideControls]: hideControls || !controlsNeeded,
          })}
        >
          <AscButton
            variant="white"
            className={styles.button}
            icon={<ArrowLeftIcon />}
            disabled={prevButtonDisabled || loading}
            onClick={handleSlideLeft}
          />
          <AscButton
            variant="white"
            className={styles.button}
            icon={<ArrowRightIcon />}
            disabled={nextButtonDisabled || loading}
            onClick={handleSlideRight}
          />
        </div>
      </div>
      <div
        ref={scrollableRef}
        className={classNames(styles?.scrollable, scrollableContainerClassName)}
      >
        <div
          ref={itemsRef}
          className={classNames(styles?.items, contentClassName)}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
